import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

interface PtoSummary {
  year: number;
  eligible: number;
  usedDays: number;
  upcomingDays: number;
  pendingDays: number;
  remaining: number;
  canSubmit: boolean;
  canApprove: boolean;
  /** "sheet" while PTO comes from the team's Google Sheet (read-only here). */
  source?: "sheet";
  inSheet?: boolean;
  flexUsed?: number | null;
  flexRemaining?: number | null;
  lastSyncedAt?: string | null;
}

interface PtoRequestRow {
  id: string;
  startDate: string;
  endDate: string;
  /** Null for a FLEX (floating holiday) day from the sheet. */
  days: number | null;
  isFlex?: boolean;
  status: "pending" | "approved" | "denied";
  notes: string | null;
  reviewNotes: string | null;
}

/** "YYYY-MM-DD" parses as UTC — build the Date from parts to avoid a one-day-back shift in US timezones. */
function formatDate(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number) as [number, number, number];
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateRange(start: string, end: string): string {
  return start === end ? formatDate(start) : `${formatDate(start)} – ${formatDate(end)}`;
}

export function PtoTab({ ownerId }: { ownerId: string }) {
  const [summary, setSummary] = useState<PtoSummary | null>(null);
  const [requests, setRequests] = useState<PtoRequestRow[] | null>(null);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    const res = await fetch(`/api/owners/${ownerId}/pto-summary`);
    if (res.ok) setSummary(await res.json());
  }, [ownerId]);

  const fetchRequests = useCallback(async () => {
    const res = await fetch(`/api/owners/${ownerId}/pto-requests`);
    if (res.ok) {
      const data = await res.json();
      setRequests(data.requests);
    }
  }, [ownerId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await Promise.all([fetchSummary(), fetchRequests()]);
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchSummary, fetchRequests]);

  async function submitRequest() {
    setFormError(null);
    if (!startDate || !endDate) {
      setFormError("Pick a start and end date.");
      return;
    }
    if (endDate < startDate) {
      setFormError("End date must be on or after the start date.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/owners/${ownerId}/pto-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ startDate, endDate, notes: notes.trim() || undefined }),
      });
      if (res.ok) {
        setStartDate("");
        setEndDate("");
        setNotes("");
        setShowForm(false);
        await Promise.all([fetchSummary(), fetchRequests()]);
      } else {
        const data = await res.json().catch(() => null);
        setFormError(data?.error ?? "Could not submit request.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function cancelRequest(id: string) {
    await fetch(`/api/pto-requests/${id}`, { method: "DELETE" });
    await Promise.all([fetchSummary(), fetchRequests()]);
  }

  async function decideRequest(id: string, status: "approved" | "denied") {
    await fetch(`/api/pto-requests/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    await Promise.all([fetchSummary(), fetchRequests()]);
  }

  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders have no stable ID
          <Skeleton key={i} className="h-16 rounded-md" />
        ))}
      </div>
    );
  }

  if (!summary) {
    return <p className="text-sm text-muted-foreground">Could not load PTO information.</p>;
  }

  const today = new Date().toISOString().slice(0, 10);
  const used = requests?.filter((r) => r.status === "approved" && r.endDate < today) ?? [];
  const upcoming = requests?.filter((r) => r.status === "approved" && r.endDate >= today) ?? [];
  const pending = requests?.filter((r) => r.status === "pending") ?? [];

  if (summary.source === "sheet") {
    return <SheetPtoView summary={summary} requests={requests ?? []} today={today} />;
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatBox label="Eligible" value={summary.eligible} />
        <StatBox label="Remaining" value={summary.remaining} highlight />
        <StatBox label="Used" value={summary.usedDays} />
        <StatBox label="Upcoming" value={summary.upcomingDays} />
      </div>
      {summary.pendingDays > 0 && (
        <p className="text-xs text-amber-600">
          {summary.pendingDays} day{summary.pendingDays === 1 ? "" : "s"} pending approval
        </p>
      )}

      {summary.canSubmit && (
        <div className="space-y-3">
          {!showForm ? (
            <Button size="sm" onClick={() => setShowForm(true)}>
              Request Time Off
            </Button>
          ) : (
            <div className="max-w-md space-y-3 rounded-md border p-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Start Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">End Date</Label>
                  <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Notes (optional)</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
              </div>
              {formError && <p className="text-xs text-destructive">{formError}</p>}
              <div className="flex gap-2">
                <Button size="sm" onClick={submitRequest} disabled={submitting}>
                  {submitting ? "Submitting…" : "Submit for Approval"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setFormError(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {summary.canApprove && pending.length > 0 && (
        <PtoRequestSection
          title="Pending Approval"
          requests={pending}
          renderActions={(r) => (
            <div className="flex gap-1.5">
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs"
                onClick={() => decideRequest(r.id, "approved")}
              >
                Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs text-destructive"
                onClick={() => decideRequest(r.id, "denied")}
              >
                Deny
              </Button>
            </div>
          )}
        />
      )}

      {!summary.canApprove && summary.canSubmit && pending.length > 0 && (
        <PtoRequestSection
          title="Pending Approval"
          requests={pending}
          renderActions={(r) => (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={() => cancelRequest(r.id)}
            >
              Cancel
            </Button>
          )}
        />
      )}

      <PtoRequestSection title="Upcoming" requests={upcoming} emptyText="No upcoming time off." />
      <PtoRequestSection title="Used" requests={used} emptyText="No PTO used yet this year." />
    </div>
  );
}

function fmtCheckedAt(iso: string | null | undefined): string {
  if (!iso) return "not yet";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// Read-only PTO from the team's Google Sheet: the sheet's own balance plus its
// request log. Requests are made in the sheet, so there's no form here.
function SheetPtoView({
  summary,
  requests,
  today,
}: {
  summary: PtoSummary;
  requests: PtoRequestRow[];
  today: string;
}) {
  const requested = requests.filter((r) => r.status === "pending");
  const upcoming = requests.filter((r) => r.status === "approved" && r.endDate >= today);
  const taken = requests.filter((r) => r.status === "approved" && r.endDate < today).reverse();
  const flex =
    summary.flexUsed != null || summary.flexRemaining != null
      ? `${summary.flexUsed ?? 0} used · ${summary.flexRemaining ?? 0} left`
      : null;
  return (
    <div className="max-w-2xl space-y-6">
      <p className="text-xs text-muted-foreground">
        <span className="font-medium text-green-700 dark:text-green-500">
          ● From the team PTO sheet
        </span>{" "}
        · updated hourly · last checked {fmtCheckedAt(summary.lastSyncedAt)}. Request time off the
        usual way — this tab shows what's in the sheet.
      </p>
      {!summary.inSheet ? (
        <p className="text-sm text-muted-foreground">
          This person isn't listed in the {summary.year} PTO sheet.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatBox label="Eligible" value={summary.eligible} />
          <StatBox label="Remaining" value={summary.remaining} highlight />
          <StatBox label="Used / booked" value={summary.usedDays} />
          <div className="rounded-md border p-3">
            <p className="text-xs text-muted-foreground">Flex Holiday</p>
            <p className="text-sm font-bold tabular-nums leading-7">{flex ?? "—"}</p>
          </div>
        </div>
      )}
      {requested.length > 0 && (
        <PtoRequestSection title="Requested (awaiting approval)" requests={requested} />
      )}
      <PtoRequestSection title="Upcoming" requests={upcoming} emptyText="No upcoming time off." />
      <PtoRequestSection title="Taken" requests={taken} emptyText="No PTO taken yet this year." />
    </div>
  );
}

function StatBox({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <div className={cn("rounded-md border p-3", highlight && "border-primary/40 bg-primary/5")}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-bold tabular-nums">{value}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: PtoRequestRow["status"] }) {
  if (status === "denied") {
    return (
      <Badge variant="secondary" className="text-destructive">
        Denied
      </Badge>
    );
  }
  return <Badge variant="secondary">{status === "approved" ? "Approved" : "Pending"}</Badge>;
}

function PtoRequestSection({
  title,
  requests,
  emptyText,
  renderActions,
}: {
  title: string;
  requests: PtoRequestRow[];
  emptyText?: string;
  renderActions?: (r: PtoRequestRow) => ReactNode;
}) {
  if (requests.length === 0 && !emptyText) return null;
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {requests.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <div className="space-y-1.5">
          {requests.map((r) => (
            <div
              key={r.id}
              className="flex items-center justify-between rounded-md border px-3 py-2"
            >
              <div>
                <p className="text-sm font-medium">{formatDateRange(r.startDate, r.endDate)}</p>
                <p className="text-xs text-muted-foreground">
                  {r.isFlex || r.days == null
                    ? "Flex holiday"
                    : `${r.days} day${r.days === 1 ? "" : "s"}`}
                  {r.notes ? ` — ${r.notes}` : ""}
                </p>
                {r.reviewNotes && (
                  <p className="text-xs text-muted-foreground italic">Note: {r.reviewNotes}</p>
                )}
              </div>
              {renderActions ? renderActions(r) : <StatusBadge status={r.status} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
